import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoIcon from '@mui/icons-material/Info';
import styles from '@/common/containers/TaskDetails/styles.less';
import taskStyles from '@/common/styles/taskStatus.less';
import { ReactBB } from '@/common/utils/index';
import cx from 'classnames';
import { routes } from '@/app/routing/index';
import {
  TASK_MODULES,
  TASK_STATUS,
  getTaskModuleLabelKey,
} from '@/common/constants/constants';
import { getTaskStatusLabel } from '@/common/constants/task-status';
import { useLocalizedField, useToggle } from '@/common/hooks/index';
import InfoModal from '@/common/containers/TaskDetails/components/InfoModal';
import { useGeneralMetaDataQuery } from '@/entities/meta-data';
import {
  DetailsProvider,
  Instructions,
  IntendedPlan,
  AssetValues,
  FinalDispositionDetails,
  TaskActions,
} from '@/common/containers/TaskDetails/components/index';
import ModulesDoneModal from '@/common/containers/Modals/ModulesDoneModal/index';
import { useTranslation } from 'react-i18next';
import {
  useCurrentDecedentId,
  useCurrentDecedentQueryState,
} from '@/app/providers/DecedentProvider';
import { useTaskListsQuery } from '@/entities/decedent';
import {
  useTaskQuery,
  useUpdateTaskStatusMutation,
  useUpdateTaskStepMutation,
} from '@/entities/task';

const TASK_REFERENCE_URL_PATTERN = /\[url=\/task\/([^/?#\]]+)\]/g;
const TASK_MODULE_LIST = Object.values(TASK_MODULES);

const resolveTaskReferenceUrls = (text, taskIdByUid) => {
  if (!text) {
    return text;
  }

  return text.replace(TASK_REFERENCE_URL_PATTERN, (match, uid) => {
    const taskId = taskIdByUid[uid] ?? taskIdByUid[uid.toLowerCase()];

    if (taskId == null) {
      return match;
    }

    return `[url=${routes.dashboard.taskDetails}/${taskId}]`;
  });
};

const TaskDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const decedentId = useCurrentDecedentId();
  const currentDecedentQuery = useCurrentDecedentQueryState();
  const [infoModal, toggleModal] = useToggle(false);
  const [doneModal, toggleDoneModal] = useToggle(false);
  const getLocalizedField = useLocalizedField();
  const { data: metaData = {} } = useGeneralMetaDataQuery();
  const { dropdowns = {} } = metaData;
  const taskQuery = useTaskQuery(id);
  const task = taskQuery.data || {};
  const updateTaskStatusMutation = useUpdateTaskStatusMutation();
  const updateTaskStepMutation = useUpdateTaskStepMutation();
  const autoActivatedTaskIdRef = useRef(null);
  const { status, section, references, remarks, instructions, templateType } =
    task;
  const hasTaskReferenceLinks = useMemo(
    () =>
      references?.some((item) => item.includes('[url=/task/')) ||
      instructions?.some(
        ({ description, descriptionFr }) =>
          description?.includes('[url=/task/') ||
          descriptionFr?.includes('[url=/task/')
      ),
    [instructions, references]
  );
  const taskListQueries = useTaskListsQuery(decedentId, TASK_MODULE_LIST, {
    enabled: !!decedentId && !!hasTaskReferenceLinks,
  });
  const localizedObjectives = getLocalizedField(task, 'objectives');
  const localizedAdditionalInfo = getLocalizedField(task, 'additionalInfo');
  const statusLabel = getTaskStatusLabel(t, status);
  const taskIdByUid = useMemo(
    () =>
      taskListQueries
        .flatMap(({ data = [] }) => data)
        .reduce((acc, item) => {
          if (item.uid && item.id != null) {
            acc[item.uid] = item.id;
            acc[item.uid.toLowerCase()] = item.id;
          }

          return acc;
        }, {}),
    [taskListQueries]
  );
  const resolvedReferences = useMemo(
    () =>
      references?.map((item) => resolveTaskReferenceUrls(item, taskIdByUid)),
    [references, taskIdByUid]
  );
  const resolvedInstructions = useMemo(
    () =>
      instructions?.map((item) => ({
        ...item,
        description: resolveTaskReferenceUrls(item.description, taskIdByUid),
        descriptionFr: resolveTaskReferenceUrls(
          item.descriptionFr,
          taskIdByUid
        ),
      })),
    [instructions, taskIdByUid]
  );

  const getLocalizedAssetText = useCallback(
    (text) => {
      const { subtitleFieldType, subtitleValue } = task;

      if (!text || !subtitleFieldType || !subtitleValue) {
        return text;
      }

      const options =
        getLocalizedField(dropdowns, subtitleFieldType) ||
        dropdowns?.[subtitleFieldType] ||
        [];

      if (!options.length) {
        return text;
      }

      const matchedOption =
        options.find((option) => option.value === subtitleValue) ||
        options.find((option) => option.label === subtitleValue);

      if (!matchedOption?.label || matchedOption.label === subtitleValue) {
        return text;
      }

      return text.replace(subtitleValue, matchedOption.label);
    },
    [dropdowns, getLocalizedField, task]
  );

  const localizedTitle = useMemo(
    () => getLocalizedAssetText(getLocalizedField(task, 'title')),
    [getLocalizedAssetText, getLocalizedField, task]
  );

  const localizedSubtitle = useMemo(
    () => getLocalizedAssetText(getLocalizedField(task, 'subtitle')),
    [getLocalizedAssetText, getLocalizedField, task]
  );

  const updateTaskStatus = useCallback(
    async (status) => {
      await updateTaskStatusMutation.mutateAsync({
        taskId: id,
        decedentId,
        status,
      });
    },
    [decedentId, id, updateTaskStatusMutation]
  );

  const updateSubTask = useCallback(
    async (stepId, value, isLast) => {
      await updateTaskStepMutation.mutateAsync({
        stepId,
        taskId: id,
        decedentId,
        isDone: value,
      });

      if (value && isLast && section === TASK_MODULES.CLOSING) {
        if (!currentDecedentQuery.data?.decedent?.id) {
          return true;
        }

        const { data } = await currentDecedentQuery.refetch();
        if (data?.sections?.closing?.task?.passed) {
          toggleDoneModal(true);
        }
      }

      return true;
    },
    [
      currentDecedentQuery,
      decedentId,
      id,
      section,
      toggleDoneModal,
      updateTaskStepMutation,
    ]
  );

  useEffect(() => {
    if (status !== TASK_STATUS.NEW) {
      autoActivatedTaskIdRef.current = null;
      return;
    }

    if (
      autoActivatedTaskIdRef.current === id ||
      updateTaskStatusMutation.isPending
    ) {
      return;
    }

    autoActivatedTaskIdRef.current = id;
    updateTaskStatus(TASK_STATUS.ONGOING).catch(() => {
      autoActivatedTaskIdRef.current = null;
    });
  }, [id, status, updateTaskStatus, updateTaskStatusMutation.isPending]);

  const nonActive = [
    TASK_STATUS.DEACTIVATED,
    TASK_STATUS.ON_HOLD,
    TASK_STATUS.SUSPENDED,
  ].includes(status);
  return (
    <DetailsProvider refreshTask={taskQuery.refetch} section={section}>
      <div className={styles.container}>
        <InfoModal
          open={infoModal}
          onClose={toggleModal}
          data={localizedAdditionalInfo}
        />
        <ModulesDoneModal open={doneModal} onClose={toggleDoneModal} />
        <Link
          className="linkBack"
          to={`${routes.dashboard.taskList}/${section || ''}`}
        >
          <ArrowBackIcon /> {t('Back to tasks list')}
        </Link>
        {localizedTitle && (
          <>
            <div className={styles.header}>
              <div className={styles.headerItem}>
                <div className={styles.headerTitle}>
                  <h3
                    className={cx(
                      nonActive && styles['headerTitle-deactivated']
                    )}
                  >
                    {ReactBB(localizedTitle)}
                  </h3>
                  <div
                    className={cx(
                      styles.headerStatus,
                      taskStyles.status,
                      taskStyles[`status-${status}`]
                    )}
                  >
                    {statusLabel}
                  </div>
                </div>
                {localizedSubtitle && (
                  <span className={styles.headerTitleSubTitle}>
                    {ReactBB(localizedSubtitle)}
                  </span>
                )}
              </div>
            </div>
            <div className={styles.body}>
              <div className={styles.bodyLeft}>
                <FinalDispositionDetails taskUid={task.uid} />
                <div className={styles.infoBlock}>
                  <div className={styles.infoBlockHeader}>
                    <h5 className={styles.infoBlockHeaderTitle}>
                      {t('Objectives')}
                    </h5>
                    {localizedAdditionalInfo && (
                      <div
                        className={styles.additionalInfo}
                        onClick={toggleModal}
                      >
                        <InfoIcon />
                        {t('Additional info')}
                      </div>
                    )}
                  </div>
                  <div className={cx(styles.infoBlockBody, styles.objectives)}>
                    {ReactBB(localizedObjectives)}
                  </div>
                </div>
                <div className={styles.infoBlock}>
                  <div className={styles.infoBlockHeader}>
                    <h5 className={styles.infoBlockHeaderTitle}>
                      {t('Instructions')}
                    </h5>
                  </div>
                  <div className={styles.infoBlockBody}>
                    <Instructions
                      list={resolvedInstructions}
                      onUpdate={updateSubTask}
                      disabled={nonActive || status === TASK_STATUS.SUSPENDED}
                      taskUid={task.uid}
                    />
                  </div>
                </div>
              </div>
              <div className={styles.bodyRight}>
                <div className={styles.infoBlock}>
                  <div className={styles.infoBlockHeader}>
                    <h5 className={styles.infoBlockHeaderTitle}>
                      {t('Details')}
                    </h5>
                    {task.uid && <b>{task.uid}</b>}
                  </div>
                  <div className={styles.infoBlockBody}>
                    <div className={styles.detail}>
                      <span>{t('Module')}:</span>
                      <span>{t(getTaskModuleLabelKey(section))}</span>
                    </div>
                  </div>
                </div>
                {resolvedReferences && (
                  <div className={styles.infoBlock}>
                    <div className={styles.infoBlockHeader}>
                      <h5 className={styles.infoBlockHeaderTitle}>
                        {t('References')}
                      </h5>
                    </div>
                    <div className={styles.infoBlockBody}>
                      <ul className={styles.references}>
                        {resolvedReferences.map((item) => (
                          <li key={item}>{ReactBB(item)}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {remarks && (
                  <div className={styles.infoBlock}>
                    <div className={styles.infoBlockHeader}>
                      <h5 className={styles.infoBlockHeaderTitle}>
                        {t('Remarks')}
                      </h5>
                    </div>
                    <div className={styles.infoBlockBody}>{remarks}</div>
                  </div>
                )}
                <IntendedPlan type={templateType} />
                <AssetValues type={templateType} />
                {status !== TASK_STATUS.COMPLETED &&
                  status !== TASK_STATUS.SUSPENDED && (
                    <TaskActions
                      isReleaseMode={nonActive}
                      isDeactivated={status === TASK_STATUS.DEACTIVATED}
                      isReleaseLoading={
                        updateTaskStatusMutation.isPending &&
                        updateTaskStatusMutation.variables?.status ===
                          TASK_STATUS.ONGOING
                      }
                      isHoldLoading={
                        updateTaskStatusMutation.isPending &&
                        updateTaskStatusMutation.variables?.status ===
                          TASK_STATUS.ON_HOLD
                      }
                      isDeactivateLoading={
                        updateTaskStatusMutation.isPending &&
                        updateTaskStatusMutation.variables?.status ===
                          TASK_STATUS.DEACTIVATED
                      }
                      onRelease={() => updateTaskStatus(TASK_STATUS.ONGOING)}
                      onHold={() => updateTaskStatus(TASK_STATUS.ON_HOLD)}
                      onDeactivate={() =>
                        updateTaskStatus(TASK_STATUS.DEACTIVATED)
                      }
                    />
                  )}
              </div>
            </div>
          </>
        )}
      </div>
    </DetailsProvider>
  );
};

export default TaskDetails;
